import { Connection } from '@3d-scene/model3d/connection';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ConnectionAreaVO, MetaElementUsageIdentityVO, MetaElementVO } from '@shared/vo/meta-element.vo';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { BaseDynamicLabelingArticleConnectProcessor } from './base-dynamic-labeling-article-connect.processor';
import { ArticleVO } from '@article/model/vo/article.vo';
import { ConnectArticlePrepareData } from '../insert/connect-article-prepare-data';
import { ArticleContainersWrapper } from '@3d-scene/model3d/article-containers-wrapper';
import { Injectable } from '@angular/core';
import { AcceptingConnectionSortGroupHelper } from '@3d-scene/helper/connection/accepting-connection-sort-group.helper';
import { ArticleContainerFactory } from '@3d-scene/model3d/article-container.factory';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { LabelingUtils } from '@3d-scene/model3d/util/labeling-utils';
import { ArticleManager } from '@article/manager/article.manager';
import { CompositeArticleCacheManager } from '@article/manager/composite-article-cache.manager';
import { ArticleContainerLoader } from '@article/service/article-container-loader';
import { CompositeArticleUtils } from '@article/util/composite-article-utils';
import { CreateLabelingHelper } from '@project/components/label/helper/create-labeling.helper';
import { LabelingArticleRotationHelper } from '@project/components/label/helper/labeling-article-rotation.helper';
import { ProjectModel } from '@project/model/project.model';
import { AlertHelper } from '@shared/alert/alert.helper';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { OverlayService } from '@shared/overlay/overlay.service';
import { ArticleUtils } from '@article/util/article-utils';
import { ArticleChangedService } from '@3d-scene/service/article-changed.service';
import { ProjectModelService } from '@project/service/project-model.service';
import { ProjectArticleService } from '@project/service/project-article.service';
import { ProjectMetaElementService } from '@project/manager/project-meta-element.service';
import { ProjectHelper } from '@project/helper/project.helper';
import { DetachArticleUsageService } from '@project/service/detach-article-usage.service';
import { ArticleContainerService } from '@3d-scene/service/article-container.service';
import { ConnectionAreaUsageUtils } from '@shared/util/connection-area-usage-utils';
import { CompositeArticleInfoUtils } from '@article/util/composite-article-info-utils';
import { ArticleUsageService } from '@article/service/article-usage.service';

@Injectable({ providedIn: 'root' })
export class UnusedDynamicLabelingArticleConnectProcessor extends BaseDynamicLabelingArticleConnectProcessor {
  constructor(
    projectModel: ProjectModel,
    projectModelService: ProjectModelService,
    projectArticleService: ProjectArticleService,
    projectMetaElementService: ProjectMetaElementService,
    localeResourceManager: LocaleResourceManager,
    projectHelper: ProjectHelper,
    alertHelper: AlertHelper,
    articleContainerLoader: ArticleContainerLoader,
    compositeArticleCacheManager: CompositeArticleCacheManager,
    articleContainerFactory: ArticleContainerFactory,
    articleManager: ArticleManager,
    compositeArticleUtils: CompositeArticleUtils,
    compositeArticleInfoUtils: CompositeArticleInfoUtils,
    articleContainerUtils: ArticleContainerUtils,
    acceptingConnectionSortGroupHelper: AcceptingConnectionSortGroupHelper,
    containerService: ContainerService,
    createLabelingHelper: CreateLabelingHelper,
    overlayService: OverlayService,
    labelingArticleRotationHelper: LabelingArticleRotationHelper,
    labelingUtils: LabelingUtils,
    articleChangedService: ArticleChangedService,
    detachArticleUsageService: DetachArticleUsageService,
    articleContainerService: ArticleContainerService,
    articleUsageService: ArticleUsageService,
  ) {
    super(
      projectModel,
      projectModelService,
      projectArticleService,
      projectMetaElementService,
      localeResourceManager,
      articleContainerLoader,
      compositeArticleCacheManager,
      articleContainerFactory,
      articleManager,
      compositeArticleUtils,
      compositeArticleInfoUtils,
      articleContainerUtils,
      acceptingConnectionSortGroupHelper,
      containerService,
      articleChangedService,
      detachArticleUsageService,
      projectHelper,
      alertHelper,
      createLabelingHelper,
      overlayService,
      labelingArticleRotationHelper,
      labelingUtils,
      articleContainerService,
      articleUsageService,
    );
  }

  protected internalPreProcess(article: ArticleVO, connectPrepareData: ConnectArticlePrepareData, handler: Function): void {
    const articleWrappers: ArticleContainersWrapper[] = super.prepareDynamicLabelingArticleWrappers(article, connectPrepareData);
    handler(articleWrappers);
  }

  protected finalizeProcess(
    baseContainer: ArticleContainer,
    acceptingConnectionCubes: Connection[],
    originalDynamicMetaElements: MetaElementVO[],
    updateTextures: boolean
  ): void {
    this.createUsageForGivingConnections(baseContainer, acceptingConnectionCubes);
    this._articleChangedService.articleChanged(baseContainer.articleGUID, updateTextures);
  }

  private createUsageForGivingConnections(givingArticleContainer: ArticleContainer, acceptingConnectionCubes: Connection[]): void {
    const connectionAreas: ConnectionAreaVO[] = ArticleUtils.getGivingConnectionsInActiveSet(
      givingArticleContainer.article,
      givingArticleContainer.articleUsage
    );
    if (this._isInversedDirection) {
      acceptingConnectionCubes.reverse();
    }
    for (let i: number = 0; i < acceptingConnectionCubes.length; i++) {
      const acceptingCube: Connection = acceptingConnectionCubes[i];
      const acceptingArticleContainer: ArticleContainer = this.containerService.getArticleContainer(acceptingCube);
      const givingConnectionAreaUsage = this.projectMetaElementService.addConnectionAreaUsageToArticle(
        givingArticleContainer.articleUsage,
        connectionAreas[i]
      );
      ConnectionAreaUsageUtils.setBackwardConnection(givingConnectionAreaUsage, MetaElementUsageIdentityVO.create(acceptingArticleContainer.articleGUID, acceptingCube.metaElementVO.name));
    }
  }
}
